import { Link, LinkProps } from "@tanstack/react-router"
import { Check, ChevronDown, LucideIcon } from "lucide-react"
import React, { ReactElement } from "react"
import { Button } from "../atoms/Button"
import { Label } from "../atoms/Label"
import { Popover, PopoverContent, PopoverTrigger } from "../atoms/Popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "../molecules/Command"
import { cn } from "../utils"

type Option = {
  value: string
  label: string | ReactElement
  muted?: boolean
  image?: string | null
}

type Props = {
  options: Option[]
  value: Option | undefined
  onChange: (option: Option | undefined) => void
  placeholder: string
  emptyPlaceholder: string
  searchPlaceholder: string
  disabled?: boolean
  searchQuery: string
  setSearchQuery: (value: string) => void
  className?: string
  closeOnSelect?: boolean
  bottomNote?: React.ReactNode
  label?: React.ReactNode
  actions?: ({ icon: LucideIcon; text: string } & LinkProps)[]
  icon?: ReactElement
  shouldFilter?: boolean
  allowCustomValue?: boolean
  customValueLabel?: (query: string) => string
  helperText?: React.ReactNode
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder,
  emptyPlaceholder,
  searchPlaceholder,
  disabled,
  searchQuery,
  setSearchQuery,
  className,
  closeOnSelect = true,
  bottomNote,
  label,
  actions,
  icon,
  shouldFilter = true,
  allowCustomValue = false,
  customValueLabel,
  helperText,
}: Props) {
  const [open, setOpen] = React.useState(false)

  function onSelect(option: Option) {
    if (closeOnSelect) {
      setOpen(false)
    }

    const newValue = value?.value === option.value ? undefined : option
    onChange(newValue)
  }

  const trimmedQuery = searchQuery.trim()
  const hasExactMatch = options.some((o) => o.value === trimmedQuery)
  const showCustomValueItem = allowCustomValue && trimmedQuery !== "" && !hasExactMatch

  function commitCustomValue() {
    if (!showCustomValueItem) return
    onChange({ value: trimmedQuery, label: trimmedQuery })
    if (closeOnSelect) setOpen(false)
  }

  return (
    <div className="flex flex-col gap-1 w-full">
      {label && <Label>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            variant="outline"
            size={icon ? "icon" : "default"}
            role="combobox"
            className={cn("justify-between font-normal h-full shadow-none", className)}
          >
            {icon ? (
              icon
            ) : (
              <>
                {value?.image && (
                  <img src={value.image} className="w-[18px] h-[18px] rounded-full" />
                )}
                <span
                  className={cn("truncate w-full text-left", {
                    "text-muted-foreground": value?.muted,
                  })}
                >
                  {value?.label ||
                    (allowCustomValue && trimmedQuery !== "" ? trimmedQuery : placeholder)}
                </span>
                <ChevronDown className="h-5 w-5 shrink-0" />
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-[var(--radix-popover-trigger-width)] min-w-72"
          align="start"
        >
          <Command shouldFilter={shouldFilter}>
            <CommandInput
              placeholder={searchPlaceholder}
              value={searchQuery}
              onValueChange={setSearchQuery}
              onKeyDown={(e) => {
                if (e.key === "Enter" && showCustomValueItem) {
                  e.preventDefault()
                  commitCustomValue()
                }
              }}
            />
            {helperText && (
              <div className="px-3 py-2 text-xs leading-snug text-muted-foreground border-b whitespace-normal">
                {helperText}
              </div>
            )}
            <CommandList>
              <CommandEmpty className="text-muted-foreground text-center pt-2 text-xs">
                {emptyPlaceholder}
              </CommandEmpty>
              {showCustomValueItem && (
                <CommandGroup>
                  <CommandItem value={`__custom__${trimmedQuery}`} onSelect={commitCustomValue}>
                    <span className="ml-2 text-primary">
                      {customValueLabel ? customValueLabel(trimmedQuery) : `Use "${trimmedQuery}"`}
                    </span>
                  </CommandItem>
                </CommandGroup>
              )}
              <CommandGroup
                key={`group-${options.length}-${options.map((o) => o.value).join(",")}`}
              >
                {options.map((option, index) => (
                  <CommandItem
                    value={option.value.replace(/"/g, "'")}
                    key={`${option.value}::${index}`}
                    onSelect={() => onSelect(option)}
                    className={cn({
                      "text-muted-foreground": option.muted,
                    })}
                  >
                    <Check
                      className={cn("ml-2 h-4 w-4", {
                        "opacity-100": value?.value === option.value,
                        "opacity-0": value?.value !== option.value,
                      })}
                    />
                    <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                      {option.image && (
                        <img src={option.image} className="w-[18px] h-[18px] rounded-full" />
                      )}
                      <span className="leading-snug truncate">{option.label}</span>
                    </div>
                  </CommandItem>
                ))}
                {actions?.map(({ text, icon: Icon, ...link }) => (
                  <CommandItem key={text}>
                    <Link {...link} className="flex gap-2 items-center text-primary">
                      <Icon />
                      {text}
                    </Link>
                  </CommandItem>
                ))}
              </CommandGroup>
              {bottomNote && <CommandShortcut>{bottomNote}</CommandShortcut>}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
